import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import leadsApi from "../api/leadsApi";
import campaignApi from "../api/campaignApi";
import { useAuthStore } from "../store/useAuthStore";
import "./CrmDashboard.css";

const STAGES = [
  "New",
  "Intro Email Sent",
  "Portfolio Sent",
  "Meeting Done",
  "Proposal Sent",
  "Negotiation",
  "Won",
];

const STATUS_LABELS = {
  New: "New",
  "Intro Email Sent": "Contacted",
  "Portfolio Sent": "Portfolio",
  "Meeting Done": "Meeting",
  "Proposal Sent": "Proposal Sent",
  Negotiation: "Negotiation",
  Won: "Won",
  Lost: "Lost",
};

const asArray = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.leads)) return response.leads;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.leads)) return response.data.leads;
  return [];
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getCurrentWeekRange = () => {
  const now = new Date();

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  const currentDay = start.getDay();
  const differenceToMonday = currentDay === 0 ? -6 : 1 - currentDay;
  start.setDate(start.getDate() + differenceToMonday);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const isWithinDateRange = (value, start, end) => {
  if (!value) return false;

  const date = new Date(value);

  return !Number.isNaN(date.getTime()) && date >= start && date <= end;
};

const isToday = (value) => {
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();
  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const getLeadName = (lead) => {
  const name = `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim();
  return name || lead?.contact || lead?.company || "Unnamed Lead";
};

const getValue = (lead) => {
  const value = Number(lead?.proposalValue || lead?.estValue || 0);
  return Number.isFinite(value) ? value : 0;
};

const isLeadActive = (lead) => {
  if (typeof lead?.isActive === "boolean") return lead.isActive;
  if (typeof lead?.active === "boolean") return lead.active;

  const state = String(
    lead?.leadState || lead?.activationStatus || lead?.activeStatus || "",
  )
    .trim()
    .toLowerCase();

  if (state === "inactive") return false;
  if (state === "active") return true;

  return true;
};

const getAssigned = (lead) => {
  if (!lead?.assignedTo) return "Not assigned";
  if (typeof lead.assignedTo === "string") return lead.assignedTo;
  return (
    lead.assignedTo.name ||
    lead.assignedTo.fullName ||
    lead.assignedTo.email ||
    "Not assigned"
  );
};

// --- period filter (Month / Quarter / Year) -----------------------------

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const QUARTERS = [
  { key: "q1", months: [0, 1, 2] }, // Jan-Mar
  { key: "q2", months: [3, 4, 5] }, // Apr-Jun
  { key: "q3", months: [6, 7, 8] }, // Jul-Sep
  { key: "q4", months: [9, 10, 11] }, // Oct-Dec
];

const getQuarterLabel = (quarter, year) => {
  const startMonth = MONTH_NAMES[quarter.months[0]];
  const endMonth = MONTH_NAMES[quarter.months[2]];
  const shortYear = String(year).slice(-2);
  return `${startMonth}-${endMonth}-${shortYear}`;
};

const getLeadPeriodDate = (lead) =>
  lead?.wonAt ||
  lead?.proposalSentAt ||
  lead?.proposalDate ||
  lead?.meetingDate ||
  lead?.nextFollowUp ||
  lead?.updatedAt ||
  lead?.createdAt ||
  lead?.leadCreatedDate;

// Year dropdown list: current year + every year found in the leads data.
// This keeps the current year always visible (even with no data yet) and
// automatically grows as leads from new years get created.
const getAvailableYears = (leads) => {
  const years = new Set([new Date().getFullYear()]);

  leads.forEach((lead) => {
    const value = getLeadPeriodDate(lead);
    if (!value) return;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      years.add(date.getFullYear());
    }
  });

  return [...years].sort((a, b) => a - b);
};

const isWithinFilter = (lead, { year, month, quarter, weekly }) => {
  const value = getLeadPeriodDate(lead);

  // "This Week" is a standalone quick filter — when it's on, Year/Month/
  // Quarter are ignored (the UI resets them to "all" as soon as Weekly is
  // toggled on, so this is mostly a safety net).
  if (weekly) {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const { start, end } = getCurrentWeekRange();
    return date >= start && date <= end;
  }

  if (year === "all" && month === "all" && quarter === "all") return true;

  if (!value) return false;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  if (year !== "all" && date.getFullYear() !== Number(year)) return false;

  if (month !== "all") {
    if (date.getMonth() !== Number(month)) return false;
  } else if (quarter !== "all") {
    const q = QUARTERS.find((item) => item.key === quarter);
    if (q && !q.months.includes(date.getMonth())) return false;
  }

  return true;
};

// Short label shown inside each KPI card under the number, reflecting the
// currently active Weekly / Year / Month / Quarter selection.
const getFilterLabel = ({ year, month, quarter, weekly }) => {
  if (weekly) return "This Week";

  if (month !== "all") {
    const monthLabel = MONTH_NAMES[Number(month)];
    return year === "all" ? monthLabel : `${monthLabel} ${year}`;
  }

  if (quarter !== "all") {
    const q = QUARTERS.find((item) => item.key === quarter);
    return getQuarterLabel(q, year === "all" ? new Date().getFullYear() : year);
  }

  if (year !== "all") return String(year);

  return "All time";
};

const initials = (value) =>
  String(value || "LD")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

const CrmDashboard = () => {
  const navigate = useNavigate();
  const { authUser } = useAuthStore();

  const currentRole = String(authUser?.role || "")
    .trim()
    .toLowerCase();

  const isAdmin = currentRole === "admin";

  const [leads, setLeads] = useState([]);
  const [filterYear, setFilterYear] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterQuarter, setFilterQuarter] = useState("all");
  const [filterWeekly, setFilterWeekly] = useState(false);
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [filterLeadState, setFilterLeadState] = useState("all");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [leadResponse, campaignResponse] = await Promise.all([
        leadsApi.getAll(),
        campaignApi.getAll(),
      ]);

      setLeads(asArray(leadResponse));

      const campaignRecords =
        campaignResponse?.campaigns ||
        campaignResponse?.data?.campaigns ||
        campaignResponse?.data ||
        campaignResponse ||
        [];

      setCampaigns(Array.isArray(campaignRecords) ? campaignRecords : []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load CRM dashboard.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const availableYears = useMemo(() => getAvailableYears(leads), [leads]);

  const availableCampaigns = useMemo(() => {
    const masterNames = campaigns
      .filter((item) => item?.isActive !== false)
      .map((item) => String(item?.name || "").trim())
      .filter(Boolean);

    const leadCampaigns = leads
      .map((lead) => String(lead?.campaign || "").trim())
      .filter(Boolean);

    return [...new Set([...masterNames, ...leadCampaigns])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [campaigns, leads]);

  const periodLabel = useMemo(() => {
    const dateLabel = getFilterLabel({
      year: filterYear,
      month: filterMonth,
      quarter: filterQuarter,
      weekly: filterWeekly,
    });

    const campaignLabel =
      filterCampaign === "all" ? dateLabel : `${dateLabel} • ${filterCampaign}`;

    if (filterLeadState === "active") {
      return `${campaignLabel} • Active`;
    }

    if (filterLeadState === "inactive") {
      return `${campaignLabel} • Inactive`;
    }

    return campaignLabel;
  }, [
    filterYear,
    filterMonth,
    filterQuarter,
    filterWeekly,
    filterCampaign,
    filterLeadState,
  ]);

  const campaignFilteredLeads = useMemo(
    () =>
      leads.filter((lead) => {
        if (filterCampaign === "all") {
          return true;
        }

        return String(lead?.campaign || "").trim() === filterCampaign;
      }),
    [leads, filterCampaign],
  );

  const stateFilteredLeads = useMemo(() => {
    if (filterLeadState === "all") {
      return campaignFilteredLeads;
    }

    const wantsActive = filterLeadState === "active";

    return campaignFilteredLeads.filter(
      (lead) => isLeadActive(lead) === wantsActive,
    );
  }, [campaignFilteredLeads, filterLeadState]);

  const filteredLeads = useMemo(
    () =>
      stateFilteredLeads.filter((lead) =>
        isWithinFilter(lead, {
          year: filterYear,
          month: filterMonth,
          quarter: filterQuarter,
          weekly: filterWeekly,
        }),
      ),
    [stateFilteredLeads, filterYear, filterMonth, filterQuarter, filterWeekly],
  );

  const data = useMemo(() => {
    const now = new Date();
    const wonLeads = filteredLeads.filter((lead) => lead.status === "Won");
    const wonSales = wonLeads.reduce((sum, lead) => sum + getValue(lead), 0);

    const { start: weekStart, end: weekEnd } = getCurrentWeekRange();

    const meetingsThisWeek = campaignFilteredLeads
      .filter((lead) => isWithinDateRange(lead.meetingDate, weekStart, weekEnd))
      .sort(
        (a, b) =>
          new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime(),
      )
      .slice(0, 8);

    const pipeline = STAGES.map((stage) => {
      const items = filteredLeads.filter((lead) => lead.status === stage);
      return {
        stage,
        count: items.length,
        value: items.reduce((sum, lead) => sum + getValue(lead), 0),
      };
    });

    const followUps = filteredLeads
      .filter((lead) => lead.nextFollowUp)
      .sort(
        (a, b) =>
          new Date(a.nextFollowUp).getTime() -
          new Date(b.nextFollowUp).getTime(),
      )
      .slice(0, 5);

    const recent = [...filteredLeads]
      .sort(
        (a, b) =>
          new Date(b.createdAt || b.leadCreatedDate || 0).getTime() -
          new Date(a.createdAt || a.leadCreatedDate || 0).getTime(),
      )
      .slice(0, 5);

    const proposals = filteredLeads
      .filter(
        (lead) =>
          lead.status === "Proposal Sent" ||
          lead.proposalSent ||
          lead.proposalSentAt,
      )
      .sort(
        (a, b) =>
          new Date(
            b.proposalSentAt || b.proposalDate || b.updatedAt || 0,
          ).getTime() -
          new Date(
            a.proposalSentAt || a.proposalDate || a.updatedAt || 0,
          ).getTime(),
      )
      .slice(0, 5);

    const sourceMap = {};
    filteredLeads.forEach((lead) => {
      const source = lead.source || "Unknown";
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const sources = Object.entries(sourceMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const teamMap = {};
    wonLeads.forEach((lead) => {
      const name = getAssigned(lead);
      if (!teamMap[name]) teamMap[name] = { name, wins: 0, sales: 0 };
      teamMap[name].wins += 1;
      teamMap[name].sales += getValue(lead);
    });

    const team = Object.values(teamMap)
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 4);

    const monthlySales = Array.from({ length: 12 }, (_, month) => ({
      label: new Date(now.getFullYear(), month, 1).toLocaleString("en-US", {
        month: "short",
      }),
      value: 0,
    }));

    const monthlyWonCounts = Array.from({ length: 12 }, (_, month) => ({
      label: new Date(now.getFullYear(), month, 1).toLocaleString("en-US", {
        month: "short",
      }),
      value: 0,
    }));

    wonLeads.forEach((lead) => {
      const date = new Date(lead.wonAt || lead.updatedAt || lead.createdAt);
      if (
        !Number.isNaN(date.getTime()) &&
        date.getFullYear() === now.getFullYear()
      ) {
        monthlySales[date.getMonth()].value += getValue(lead);
        monthlyWonCounts[date.getMonth()].value += 1;
      }
    });

    return {
      total: filteredLeads.length,
      activeCount: filteredLeads.filter((lead) => isLeadActive(lead)).length,
      inactiveCount: filteredLeads.filter((lead) => !isLeadActive(lead)).length,
      newLeads: filteredLeads.filter((lead) => lead.status === "New").length,
      followUpsDue: filteredLeads.filter((lead) => {
        if (!lead.nextFollowUp) return false;
        const date = new Date(lead.nextFollowUp);
        return (
          !Number.isNaN(date.getTime()) &&
          date <= now &&
          !["Won", "Lost"].includes(lead.status)
        );
      }).length,
      meetingsToday: campaignFilteredLeads.filter((lead) =>
        isToday(lead.meetingDate),
      ).length,
      proposalsSent: filteredLeads.filter(
        (lead) =>
          lead.status === "Proposal Sent" ||
          lead.proposalSent ||
          lead.proposalSentAt,
      ).length,
      wonCount: wonLeads.length,
      wonSales,
      pipeline,
      followUps,
      meetingsThisWeek,
      recent,
      proposals,
      sources,
      team,
      monthlySales,
      monthlyWonCounts,
      wonLeads: wonLeads.slice(0, 5),
    };
  }, [filteredLeads, campaignFilteredLeads]);

  const visiblePipeline = useMemo(
    () =>
      isAdmin
        ? data.pipeline
        : data.pipeline.filter((row) => row.stage !== "Proposal Sent"),
    [data.pipeline, isAdmin],
  );

  return (
    <div className="crm-dashboard-page">
      <div className="crm-dashboard-topbar">
        <div>
          <h1>CRM Dashboard</h1>
          <p>Manage your leads, clients and won sales.</p>
        </div>

        <div className="crm-dashboard-top-actions">
          <div className="crm-period-filter">
            <span>Filter</span>

            <button
              type="button"
              className={
                filterWeekly
                  ? "crm-period-button crm-period-button-active"
                  : "crm-period-button"
              }
              onClick={() => {
                setFilterWeekly((prev) => {
                  const next = !prev;
                  if (next) {
                    // Weekly is a standalone quick filter — turning it on
                    // clears Year/Month/Quarter so they don't fight it.
                    setFilterYear("all");
                    setFilterMonth("all");
                    setFilterQuarter("all");
                  }
                  return next;
                });
              }}
            >
              This Week
            </button>

            <select
              className="crm-period-select"
              value={filterYear}
              disabled={filterWeekly}
              onChange={(e) => {
                setFilterWeekly(false);
                setFilterYear(e.target.value);
              }}
            >
              <option value="all">All Years</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <select
              className="crm-period-select"
              value={filterMonth}
              disabled={filterWeekly}
              onChange={(e) => {
                setFilterWeekly(false);
                setFilterMonth(e.target.value);
                setFilterQuarter("all");
              }}
            >
              <option value="all">All Months</option>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>

            <select
              className="crm-period-select"
              value={filterQuarter}
              disabled={filterWeekly}
              onChange={(e) => {
                setFilterWeekly(false);
                setFilterQuarter(e.target.value);
                setFilterMonth("all");
              }}
            >
              <option value="all">All Quarters</option>
              {QUARTERS.map((q) => (
                <option key={q.key} value={q.key}>
                  {getQuarterLabel(
                    q,
                    filterYear === "all"
                      ? new Date().getFullYear()
                      : filterYear,
                  )}
                </option>
              ))}
            </select>

            <select
              className="crm-period-select"
              value={filterCampaign}
              onChange={(e) => setFilterCampaign(e.target.value)}
              title="Filter dashboard by campaign"
            >
              <option value="all">All Campaigns</option>
              {availableCampaigns.map((campaign) => (
                <option key={campaign} value={campaign}>
                  {campaign}
                </option>
              ))}
            </select>

            <select
              className="crm-period-select"
              value={filterLeadState}
              onChange={(e) => setFilterLeadState(e.target.value)}
              title="Filter dashboard by lead activation"
            >
              <option value="all">All Leads</option>
              <option value="active">Active Leads</option>
              <option value="inactive">Inactive Leads</option>
            </select>
          </div>

          <button
            type="button"
            className="crm-outline-btn"
            onClick={() => navigate("/leads")}
          >
            ⇩ Import Leads
          </button>
          <button
            type="button"
            className="crm-primary-btn"
            onClick={() => navigate("/leads", { state: { openAddLead: true } })}
          >
            ＋ Add Lead
          </button>
        </div>
      </div>

      {error && <div className="crm-dashboard-error">{error}</div>}

      <div className="crm-kpi-grid">
        <Kpi
          title="Total Leads"
          value={data.total}
          icon="♙"
          tone="blue"
          periodLabel={periodLabel}
        />
        <Kpi
          title="Active Leads"
          value={data.activeCount}
          icon="✓"
          tone="green"
          periodLabel={periodLabel}
        />
        <Kpi
          title="Inactive Leads"
          value={data.inactiveCount}
          icon="○"
          tone="orange"
          periodLabel={periodLabel}
        />
        <Kpi
          title="New Leads"
          value={data.newLeads}
          icon="♧"
          tone="green"
          periodLabel={periodLabel}
        />
        <Kpi
          title="Follow-ups Due"
          value={data.followUpsDue}
          icon="◫"
          tone="orange"
          periodLabel={periodLabel}
        />
        <Kpi
          title="Meetings Today"
          value={data.meetingsToday}
          icon="▣"
          tone="blue"
          periodLabel={periodLabel}
        />
        {isAdmin && (
          <Kpi
            title="Proposals Sent"
            value={data.proposalsSent}
            icon="▤"
            tone="purple"
            periodLabel={periodLabel}
          />
        )}
        <Kpi
          title="Projects Won"
          value={data.wonCount}
          icon="✓"
          tone="green"
          periodLabel={periodLabel}
        />
        <Kpi
          title={isAdmin ? "Won Sales" : "Won Deals"}
          value={isAdmin ? formatMoney(data.wonSales) : data.wonCount}
          icon={isAdmin ? "$" : "✓"}
          tone="red"
          periodLabel={periodLabel}
        />
      </div>

      {loading ? (
        <div className="crm-dashboard-loading">Loading CRM dashboard...</div>
      ) : (
        <>
          <div className="crm-grid crm-grid-top">
            <Card title="Sales Pipeline">
              <Funnel rows={visiblePipeline} isAdmin={isAdmin} />
            </Card>

            <Card
              title="Follow-ups Due"
              action="View all"
              onAction={() => navigate("/leads")}
            >
              <FollowUps items={data.followUps} navigate={navigate} />
            </Card>

            <Card
              title="Recent Leads"
              action="View all"
              onAction={() => navigate("/leads")}
            >
              <RecentTable
                items={data.recent}
                navigate={navigate}
                isAdmin={isAdmin}
              />
            </Card>
          </div>

          <div className="crm-grid crm-grid-middle">
            <Card title="Tasks Due This Week">
              <TaskList items={data.followUps} />
            </Card>

            <Card title="Meetings This Week">
              <MeetingList items={data.meetingsThisWeek} navigate={navigate} />
            </Card>

            <Card title="Leads by Source">
              <SourceChart items={data.sources} total={data.total} />
            </Card>

            <Card title="Top Performing Team">
              <TeamList items={data.team} isAdmin={isAdmin} />
            </Card>
          </div>

          <div
            className={`crm-grid crm-grid-bottom ${
              !isAdmin ? "crm-grid-bottom-user" : ""
            }`}
          >
            {isAdmin && (
              <Card
                title="Recent Proposals"
                action="View all"
                onAction={() => navigate("/proposals")}
              >
                <ProposalTable items={data.proposals} navigate={navigate} />
              </Card>
            )}

            <Card title="Won Projects Overview">
              <WonProjects items={data.wonLeads} navigate={navigate} />
            </Card>

            <Card title={isAdmin ? "Won Sales Overview" : "Won Deals Overview"}>
              <SalesChart
                data={isAdmin ? data.monthlySales : data.monthlyWonCounts}
                total={isAdmin ? data.wonSales : data.wonCount}
                showMoney={isAdmin}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

const Kpi = ({ title, value, icon, tone, periodLabel = "All time" }) => (
  <div className="crm-kpi-card">
    <div className={`crm-kpi-icon crm-kpi-${tone}`}>{icon}</div>
    <div>
      <span>{title}</span>
      <strong>{value}</strong>
      <small>{periodLabel}</small>
    </div>
  </div>
);

const Card = ({ title, action, onAction, children }) => (
  <div className="crm-card">
    <div className="crm-card-head">
      <h2>{title}</h2>
      {action && (
        <button type="button" onClick={onAction}>
          {action}
        </button>
      )}
    </div>
    <div className="crm-card-body">{children}</div>
  </div>
);

const Funnel = ({ rows, isAdmin }) => (
  <div className="crm-funnel-wrap">
    <div className="crm-funnel">
      {rows.map((row, index) => (
        <div
          key={row.stage}
          className={`crm-funnel-row crm-funnel-row-${index + 1}`}
        >
          {isAdmin ? (
            <span className="crm-funnel-admin-value">
              <strong>{row.count}</strong>
              <small>{formatMoney(row.value)}</small>
            </span>
          ) : (
            <strong className="crm-funnel-user-count">{row.count}</strong>
          )}
        </div>
      ))}
    </div>

    <div className="crm-funnel-legend">
      {rows.map((row, index) => (
        <div key={row.stage}>
          <span className={`crm-dot crm-dot-${index + 1}`} />
          <span>{STATUS_LABELS[row.stage] || row.stage}</span>
          <span className="crm-funnel-legend-value">
            <strong>{row.count}</strong>
            {isAdmin && <small>{formatMoney(row.value)}</small>}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const FollowUps = ({ items, navigate }) => {
  if (!items.length) return <Empty text="No follow-ups scheduled." />;

  return (
    <div className="crm-list">
      {items.map((lead) => (
        <button
          key={lead._id}
          type="button"
          onClick={() => navigate(`/leads/${lead._id}`)}
        >
          <span className="crm-avatar">{initials(getLeadName(lead))}</span>
          <span>
            <strong>{lead.company || "—"}</strong>
            <small>{lead.followUpNote || "Client follow-up"}</small>
          </span>
          <time>{formatDate(lead.nextFollowUp)}</time>
        </button>
      ))}
    </div>
  );
};

const RecentTable = ({ items, navigate, isAdmin }) => (
  <div className="crm-table-wrap">
    <table className="crm-table">
      <thead>
        <tr>
          <th>Lead ID</th>
          <th>Client Name</th>
          <th>Company</th>
          <th>Campaign</th>
          <th>Source</th>
          <th>Status</th>
          <th>Active / Inactive</th>
          <th>Assigned To</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((lead) => (
          <tr key={lead._id}>
            <td>{lead.leadId || String(lead._id || "").slice(-6)}</td>
            <td>{getLeadName(lead)}</td>
            <td>{lead.company || "—"}</td>
            <td>{lead.campaign || "—"}</td>
            <td>{lead.source || "Unknown"}</td>
            <td>
              <span
                className={`crm-status crm-status-${
                  !isAdmin && lead.status === "Proposal Sent"
                    ? "in-progress"
                    : String(lead.status || "new")
                        .toLowerCase()
                        .replaceAll(" ", "-")
                }`}
              >
                {!isAdmin && lead.status === "Proposal Sent"
                  ? "In Progress"
                  : STATUS_LABELS[lead.status] || lead.status || "New"}
              </span>
            </td>
            <td>
              <span
                className={`crm-status ${
                  isLeadActive(lead) ? "crm-status-won" : "crm-status-inactive"
                }`}
                style={
                  isLeadActive(lead)
                    ? undefined
                    : {
                        background: "#f1f5f9",
                        color: "#64748b",
                      }
                }
              >
                {isLeadActive(lead) ? "Active" : "Inactive"}
              </span>
            </td>
            <td>{getAssigned(lead)}</td>
            <td>
              <button
                type="button"
                className="crm-eye"
                onClick={() => navigate(`/leads/${lead._id}`)}
              >
                ◉
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TaskList = ({ items }) => (
  <div className="crm-task-list">
    {items.length ? (
      items.map((lead) => (
        <div key={lead._id}>
          <span className="crm-task-circle" />
          <strong>Follow up - {lead.company || getLeadName(lead)}</strong>
          <span className="crm-priority">{lead.priority || "Medium"}</span>
          <time>{formatDate(lead.nextFollowUp)}</time>
        </div>
      ))
    ) : (
      <Empty text="No tasks due." />
    )}
  </div>
);

/* =========================================================
   MEETINGS THIS WEEK — now rendered as a table instead
   of the previous card-list layout.
   ========================================================= */
const MeetingList = ({ items, navigate }) => (
  <div className="crm-meeting-table-wrap">
    <table className="crm-meeting-table">
      <thead>
        <tr>
          <th>Company</th>
          <th>Campaign</th>
          <th>Date &amp; Time</th>
          <th>Mode</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.length ? (
          items.map((lead) => (
            <tr key={lead._id} onClick={() => navigate(`/leads/${lead._id}`)}>
              <td>{lead.company || getLeadName(lead)}</td>
              <td>{lead.campaign || "—"}</td>
              <td>{formatDateTime(lead.meetingDate)}</td>
              <td>{lead.meetingMode || "—"}</td>
              <td>
                <button
                  type="button"
                  className="crm-eye"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/leads/${lead._id}`);
                  }}
                >
                  ◉
                </button>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={5}>
              <Empty text="No meetings scheduled for this week." />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const SourceChart = ({ items, total }) => {
  const colors = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
  let cursor = 0;
  const gradient = items
    .map((item, index) => {
      const start = cursor;
      cursor += total ? (item.count / total) * 100 : 0;
      return `${colors[index]} ${start}% ${cursor}%`;
    })
    .join(", ");

  return (
    <div className="crm-source-wrap">
      <div
        className="crm-donut"
        style={{
          background: `conic-gradient(${gradient || "#e5e7eb 0 100%"})`,
        }}
      >
        <div />
      </div>
      <div className="crm-source-legend">
        {items.map((item, index) => (
          <div key={item.name}>
            <span style={{ background: colors[index] }} />
            <strong>{item.name}</strong>
            <small>{item.count}</small>
          </div>
        ))}
      </div>
    </div>
  );
};

const TeamList = ({ items, isAdmin }) => {
  const max = Math.max(
    ...items.map((item) => (isAdmin ? item.sales : item.wins)),
    1,
  );

  return (
    <div className="crm-team-list">
      {items.length ? (
        items.map((item) => {
          const progressValue = isAdmin ? item.sales : item.wins;

          return (
            <div key={item.name}>
              <span className="crm-avatar">{initials(item.name)}</span>
              <div>
                <strong>{item.name}</strong>
                <small>
                  {isAdmin
                    ? `${item.wins} Won | ${formatMoney(item.sales)}`
                    : `${item.wins} Won`}
                </small>
              </div>
              <div className="crm-progress">
                <span style={{ width: `${(progressValue / max) * 100}%` }} />
              </div>
            </div>
          );
        })
      ) : (
        <Empty text="No won team data." />
      )}
    </div>
  );
};

const ProposalTable = ({ items, navigate }) => (
  <div className="crm-table-wrap">
    <table className="crm-table">
      <thead>
        <tr>
          <th>Proposal Ref.</th>
          <th>Client</th>
          <th>Company</th>
          <th>Campaign</th>
          <th>Proposal Value</th>
          <th>Status</th>
          <th>Date</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((lead) => (
          <tr key={lead._id}>
            <td>{lead.proposalReference || "—"}</td>
            <td>{getLeadName(lead)}</td>
            <td>{lead.company || "—"}</td>
            <td>{lead.campaign || "—"}</td>
            <td>{formatMoney(getValue(lead))}</td>
            <td>
              <span className="crm-status crm-status-proposal-sent">Sent</span>
            </td>
            <td>
              {formatDate(
                lead.proposalSentAt || lead.proposalDate || lead.updatedAt,
              )}
            </td>
            <td>
              <button
                type="button"
                className="crm-eye"
                onClick={() => navigate(`/leads/${lead._id}`)}
              >
                ◉
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const WonProjects = ({ items, navigate }) => (
  <div className="crm-won-list">
    {items.length ? (
      items.map((lead) => (
        <button
          key={lead._id}
          type="button"
          onClick={() => navigate(`/leads/${lead._id}`)}
        >
          <span className="crm-project-thumb">
            {initials(lead.company || getLeadName(lead))}
          </span>
          <span>
            <strong>{lead.company || getLeadName(lead)}</strong>
            <small>{getAssigned(lead)}</small>
          </span>
          <span className="crm-status crm-status-won">Won</span>
        </button>
      ))
    ) : (
      <Empty text="No won projects yet." />
    )}
  </div>
);

const SalesChart = ({ data, total, showMoney = true }) => {
  const width = 620;
  const height = 230;
  const pad = 30;
  const max = Math.max(...data.map((item) => item.value), 1);
  const points = data.map((item, index) => ({
    ...item,
    x: pad + (index / Math.max(data.length - 1, 1)) * (width - pad * 2),
    y: height - pad - (item.value / max) * (height - pad * 2),
  }));
  const path = points
    .map((point, index) => `${index ? "L" : "M"} ${point.x} ${point.y}`)
    .join(" ");

  return (
    <div className="crm-sales-chart">
      <div className="crm-sales-total">
        <strong>{showMoney ? formatMoney(total) : total}</strong>
        <span>
          {showMoney ? "Total value of Won leads" : "Total number of Won deals"}
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`}>
        {[0, 1, 2, 3].map((line) => {
          const y = pad + (line / 3) * (height - pad * 2);
          return (
            <line
              key={line}
              x1={pad}
              x2={width - pad}
              y1={y}
              y2={y}
              className="crm-grid-line"
            />
          );
        })}
        <path d={path} className="crm-sales-line" />
        {points.map((point) => (
          <circle
            key={point.label}
            cx={point.x}
            cy={point.y}
            r="4"
            className="crm-sales-dot"
          />
        ))}
      </svg>
      <div className="crm-month-labels">
        {data.map((item) => (
          <span key={item.label}>{item.label}</span>
        ))}
      </div>
    </div>
  );
};

const Empty = ({ text }) => <div className="crm-empty">{text}</div>;

export default CrmDashboard;
